import './modules/collection-context.js'
import './modules/feature-cards.js'
import './modules/product-price.js'
import './modules/deal-prices.js'
import './modules/quantity-input.js'
import './modules/search-drawer.js'
import { hydrateMediaImages } from './modules/media-image.js'
import './modules/embla-init.js'
import './modules/embla-product-init.js'
import './modules/customer-accounts.js'
import './modules/deal-picker.js'
import './modules/tin-deal-note.js'
import { shippingChoice, shippingSettled } from './modules/shipping-choice.js'
import { confirmDealFallback, dealFailPreview, startDealFail } from './modules/deal-fail.js'
import { redrawBasket } from './modules/deal-unlock.js'
import './modules/add-ons.js'
import './modules/live-stock.js'

import { startHeader } from '@split-infinity/cardo/header'
import { startBounce } from '@split-infinity/cardo/bounce'
import { startLateBackground } from '@split-infinity/cardo/late-background'
import { startCart } from '@split-infinity/cardo/cart'
import { startDrawers, openDrawer, closeDrawer } from '@split-infinity/cardo/drawer'
import { startVariants } from '@split-infinity/cardo/variants'
import { startWarm } from '@split-infinity/cardo/warm'
import { startGoals } from '@split-infinity/cardo/goal'
import { startFilters } from '@split-infinity/cardo/filter'
import { sectionIdOf } from '@split-infinity/cardo/swap'
import { beforeCheckout, startCheckout } from '@split-infinity/cardo/checkout'
import { startDeals } from '@deals/client'

window.hydrateMediaImages = hydrateMediaImages

// The theme's own modules wire themselves up on `ui:hydrate`: once for the
// page, again for a page restored from back/forward, and for every region of
// fresh HTML cardo swaps in.
const hydrate = (target) =>
  target.dispatchEvent(new CustomEvent('ui:hydrate', { bubbles: true, detail: { target } }))
document.addEventListener('cardo:render', (e) => hydrate(e.detail.root))
window.addEventListener('pageshow', (e) => e.persisted && hydrate(document))

// The menu drawer belongs to the burger, which goes away at the burger
// breakpoint - close the menu when the screen grows past it.
const closeMenuWhenWide = () => {
  const token = document.querySelector('[data-burger-breakpoint]')?.dataset.burgerBreakpoint
  const width = token && getComputedStyle(document.documentElement).getPropertyValue(`--breakpoint-${token}`).trim()
  if (!width) return
  window.matchMedia(`(min-width: ${width})`).addEventListener('change', (e) => e.matches && closeDrawer('menu'))
}

const start = () => {
  startHeader({ header: 'header[data-smart-sticky]', storageKey: 'htwh:header-hidden' })
  startBounce()
  startLateBackground()
  startDrawers()
  // The cart drawer follows every cart change, and so does the /cart page.
  const cartPage = document.querySelector('[data-cart-page]')
  startCart({ sections: ['drawer-cart', cartPage && sectionIdOf(cartPage)].filter(Boolean) })
  startVariants()
  // Phones can't hover, so cards that stay on screen fetch their product
  // page's main photo ahead (Michael 2026-10-02).
  startWarm({ whenVisible: window.matchMedia('(hover: none)').matches })
  startGoals()
  // A filtered list's count line ("1–6 of 6 Products") follows the cards
  // shown: data-filter-count holds the wording, {range} and {n} filled in.
  document.addEventListener('cardo:filter', (e) => {
    const n = e.detail.visible.length
    e.target.querySelectorAll('[data-filter-count]').forEach((el) => {
      el.textContent = el.dataset.filterCount.replace('{range}', n ? `1–${n}` : '0').replace('{n}', n)
    })
  })

  startFilters()
  closeMenuWhenWide()
  // top-lock columns (tailwind.css) carry their own height, so a column taller
  // than the space under the header locks with its bottom in view.
  const lockHeight = new ResizeObserver((entries) => {
    entries.forEach((e) => e.target.style.setProperty('--lock-height', `${e.borderBoxSize[0].blockSize}px`))
  })
  document.querySelectorAll('[class*="top-lock"]').forEach((el) => lockHeight.observe(el))
  // Pulse writes the ad a shopper came from onto the cart; make sure it has
  // before they leave for checkout (Michael 2026-10-06). Capped by cardo, so
  // checkout never waits long. No Pulse on the page = nothing held.
  beforeCheckout(() => window.pulse?.syncCart?.())
  // Deals (Michael 2026-10-06): when the theme's Deals API setting is set, our
  // deals program prices the basket and Checkout opens its prepared Shopify
  // checkout. Started BEFORE cardo's checkout hold so its capture-phase click
  // handler takes Checkout first (and stops cardo's); without the setting,
  // Checkout is Shopify's normal one, held only for Pulse.
  const dealsApi = document.documentElement.dataset.dealsApi
  if (dealsApi) {
    startDeals({
      api: dealsApi,
      // Who is signed in, for member prices: only when the theme setting is
      // filled in (once deals' MEMBER_PRICING is on); left out, the client never asks.
      identity: document.documentElement.dataset.dealsIdentity || undefined,
      attribution: () => window.pulse?.attribution?.() ?? {},
      // Pulse's ad details go on the cart before either checkout (draft or fallback).
      beforeLeave: () => window.pulse?.syncCart?.(),
      // Standard / Express (Michael 2026-10-07): the choice in the cart goes
      // with every request, so the draft carries the shipping the button showed;
      // a choice still being written is waited for at Checkout.
      shippingAttribute: '_shipping_choice',
      shippingChoice,
      settle: shippingSettled,
      // "We couldn't apply your deal" (2026-10-07, for Michael's look check):
      // a basket with a deal that can't get its deal checkout is asked Try
      // again / Email us / Pay full price instead of going silently to full
      // price. dealFailPreview is the dev theme's ?deals_fail=1 switch.
      confirmFallback: confirmDealFallback,
      forceFail: dealFailPreview,
      // The $60 tin deal for Meta ad taps (0.9.0): ?hd=<code> on the ad link
      // asks deals for a 7-day pass (src/js/modules/deal-unlock.js reads it).
      unlockParam: 'hd'
    })
    startDealFail()
    // Paid elsewhere (e.g. in another tab): the deals server emptied the cart.
    document.addEventListener('deals:paid', () => redrawBasket().catch(() => {}))
  }
  startCheckout()

  // An add opens the cart drawer - except on the deal page, where shoppers
  // add tin after tin and the page itself shows the basket. It opens on the
  // tap, saying "Adding…" until Shopify has the item (HTWH speed review
  // 2026-10-09); if the add fails (refused, or the connection dropped) it
  // closes again so the form's message shows, and focus goes back to the
  // form's button once cardo has enabled it again.
  const cartOpen = () => document.documentElement.dataset.cardoDrawerOpen === 'cart'
  const addStatus = (text) => {
    const el = document.querySelector('[data-cart-add-status]')
    if (el) el.textContent = text
  }
  let openedOnTap = false
  document.addEventListener('cardo:adding', (e) => {
    if (e.detail.context === 'deal') return
    if (!cartOpen()) {
      openDrawer('cart')
      openedOnTap = true
    }
    addStatus('Adding to your basket…')
  })
  document.addEventListener('cardo:cart-error', (e) => {
    addStatus('')
    const form = e.detail.form
    if (openedOnTap && form && cartOpen()) {
      closeDrawer('cart', { restoreFocus: false })
      setTimeout(() => form.querySelector('[type="submit"]')?.focus({ preventScroll: true }))
    }
    openedOnTap = false
  })
  document.addEventListener('cardo:added', (e) => {
    openedOnTap = false
    if (e.detail.context !== 'deal') addStatus('Added to your basket.')
    if (e.detail.context !== 'deal' && !cartOpen()) openDrawer('cart')
    // Back to 1 once added (Michael 2026-10-04): left at 3, the shipping bar
    // went on counting 3 more and said "Add these to get free express" for
    // tins already in the basket.
    const qty = e.detail.form?.querySelector('input[name="quantity"]')
    if (qty && qty.value !== '1') {
      qty.value = '1'
      qty.dispatchEvent(new Event('input', { bubbles: true }))
      qty.dispatchEvent(new Event('change', { bubbles: true }))
    }
  })

  hydrate(document)
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true })
} else {
  start()
}
